import type { ID } from "types";
import { ApiRoutes } from "consts/enpoints/api";
import { Filters } from "consts/users";
import { RoleName } from "consts/auth";
import { RequestQueryMapper } from "util/request-query-mapper";
import { fetchApi } from "infrastructure/api";
import { updateOrCreateResource } from "infrastructure/api/api";
import type {
    Ledger,
    Merchant,
    MerchantBusiness,
    MerchantDomain,
    MerchantKYCService,
    MerchantPayoutAccount,
    MerchantPayoutCryptoAccount,
    MerchantSettings
} from "features/merchants/types";

export const getMerchants = async (queryString = '') => {
    const query = new URLSearchParams(queryString);
    const pspValues = Array.from(query.entries())
        .filter(([key]) => key.startsWith('paymentMethodPSP'))
        .map(([, value]) => value);
    const apiId = Array.from(query.entries())
        .find(([key]) => key.startsWith('apiId'))?.[1]?.split(':')[0];

    Array.from(query.keys()).forEach(key => {
        if (key.startsWith('paymentMethodPSP') || key.startsWith('apiId')) query.delete(key);
    });

    let matchingMerchantIds: Set<string> | null = null;
    if (pspValues.length) {
        const response = await getMerchantsMethodsResponse();
        matchingMerchantIds = new Set(response.data.data
            .filter((pivot: any) => Boolean(pivot.enabled) && pspValues.includes(pivot.paymentMethodPSP))
            .map((pivot: any) => String(pivot.merchantId)));
    }
    if (apiId) {
        const response = await getMerchantSettingsResponse();
        const apiMerchantIds = new Set<string>(response.data.data
            .filter((settings: any) => String(settings.apiId).toLowerCase().includes(apiId.toLowerCase()))
            .map((settings: any) => String(settings.merchantId)));
        matchingMerchantIds = matchingMerchantIds
            ? new Set(Array.from(matchingMerchantIds).filter(id => apiMerchantIds.has(id)))
            : apiMerchantIds;
    }

    if (matchingMerchantIds) {
        matchingMerchantIds.forEach(id => query.append('coreId[in][]', id));
        if (!matchingMerchantIds.size) query.append('coreId[in][]', '-1');
    }

    const merchantsResponse = await fetchApi(`${ApiRoutes.GetMerchants}?${query.toString()}`);
    if (!merchantsResponse.ok) return merchantsResponse;
    const payload = await merchantsResponse.json();
    const merchants = payload.data?.data ?? [];
    const merchantIds = new Set(merchants.map((merchant: any) => String(merchant.coreId)));
    const [settingsResponse, methodsResponse] = await Promise.all([
        getMerchantSettingsResponse(),
        getMerchantsMethodsResponse()
    ]);
    const settingsByMerchant = new Map(settingsResponse.data.data
        .filter((settings: any) => merchantIds.has(String(settings.merchantId)))
        .map((settings: any) => [String(settings.merchantId), settings]));
    const pspsByMerchant = methodsResponse.data.data.reduce((map: Map<string, Set<string>>, pivot: any) => {
        if (!pivot.enabled || !merchantIds.has(String(pivot.merchantId))) return map;
        const id = String(pivot.merchantId);
        if (!map.has(id)) map.set(id, new Set());
        map.get(id)!.add(pivot.paymentMethodPSP);
        return map;
    }, new Map<string, Set<string>>());

    payload.data.data = merchants.map((merchant: any) => ({
        ...merchant,
        apiId: (settingsByMerchant.get(String(merchant.coreId)) as any)?.apiId ?? null,
        paymentMethodPSPs: Array.from(pspsByMerchant.get(String(merchant.coreId)) ?? []).sort()
    }));

    return new Response(JSON.stringify(payload), {
        status: merchantsResponse.status,
        headers: { 'Content-Type': 'application/json' }
    });
};

const getMerchantSettingsResponse = async () =>
    (await fetchApi(`${ApiRoutes.GetMerchantSettings}?perPage=1000`)).json();

const getMerchantsMethodsResponse = async () =>
    (await fetchApi(`${ApiRoutes.GetMerchantsMethodsPivot}?perPage=2000`)).json();

export const getMerchantById = (coreId: ID) =>
    fetchApi(ApiRoutes.GetMerchant(coreId));

export const getMerchantDomains = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantDomains}${queryString}`);

export const createOrUpdateMerchantDomain = (body: Partial<MerchantDomain>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostMerchantDomains,
        getId: body => body.coreId
    });

export const deleteMerchantDomain = (coreId: ID) =>
    fetchApi(ApiRoutes.DeleteMerchantDomains(coreId), {
        method: 'DELETE'
    });

export const getMerchantBusinesses = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantBusinesses()}${queryString}`);

export const getMerchantBusinessById = (coreId: ID) =>
    fetchApi(ApiRoutes.GetMerchantBusinesses(coreId));

export const createOrUpdateMerchantBusiness = (body: Partial<MerchantBusiness>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostMerchantBusinesses,
        getId: body => body.coreId
    });

export const deleteMerchantBusiness = (coreId: ID) =>
    fetchApi(ApiRoutes.DeleteMerchantBusinesses(coreId), {
        method: 'DELETE'
    });

export const getMerchantUsers = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantUsers}${
        RequestQueryMapper
            .from(queryString)
            .contains(Filters.roleName, RoleName.Merchant)
            .toString()
    }`);

export const getMerchantGroups = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantGroups}${queryString}`);

export const createOrUpdateMerchant = (body: Partial<Merchant>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostMerchant,
        getId: body => body.coreId
    });

export const getMerchantKYCSettings = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantKYCSettings}${queryString}`);

export const updateOrCreateMerchantKYCSettings = (body: Partial<MerchantKYCService> & Required<Pick<
    MerchantKYCService,
    | 'merchantId'
    | 'kycTypeId'
    | 'summedAmountEurLimit'
>>) =>
    fetchApi(ApiRoutes.PutMerchantKYCSettings(
        body.merchantId,
        body.kycTypeId
    ), {
        method: 'PUT',
        body
    });

export const getMerchantSettings = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetMerchantSettings}${queryString}`);

export const createOrUpdateMerchantSettings = (body: Partial<MerchantSettings>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PutMerchantSettings,
        getId: body => body.coreId
    });

export const getPayoutAccounts = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetPayoutAccounts}${queryString}`);

export const createOrUpdatePayoutAccount = (body: Partial<MerchantPayoutAccount>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PutPayoutAccounts,
        getId: body => body.coreId
    });

export const deletePayoutAccount = (coreId: ID) =>
    fetchApi(ApiRoutes.DeletePayoutAccounts(coreId), {
        method: 'DELETE'
    });

export const getPayoutCryptoAccounts = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetPayoutCryptoAccounts}${queryString}`);

export const createOrUpdatePayoutCryptoAccount = (body: Partial<MerchantPayoutCryptoAccount>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PutPayoutCryptoAccounts,
        getId: body => body.coreId
    });

export const deletePayoutCryptoAccount = (coreId: ID) =>
    fetchApi(ApiRoutes.DeletePayoutCryptoAccounts(coreId), {
        method: 'DELETE'
    });

export const getLedgers = (queryString = '') =>
    fetchApi(`${ApiRoutes.GetLedgers}${queryString}`);

export const createOrUpdateLedger = (body: Partial<Ledger>) =>
    updateOrCreateResource({
        body,
        getRoute: ApiRoutes.PostLedgers,
        getId: body => body.coreId
    });
